/**
 * Ticketmaster API service for frontend
 * Handles communication with the backend Ticketmaster API endpoints
 */

import { types as sdkTypes } from '../util/sdkLoader';
import { getVenueCoordinates } from './cityCoordinates';
import {
  formatLocalTimeForDisplay,
  getLocalDateParts,
  getTicketmasterLocalParts,
} from './eventLocalTime';
const { UUID } = sdkTypes;

/**
 * Search for events using Ticketmaster API
 * @param {string} keyword - Search keyword (artist, venue, event name)
 * @param {Object} options - Additional search options
 * @returns {Promise<Object>} Events data from Ticketmaster
 */
import { getTicketEvents, getTicketEventDetails } from './api';

const TRANSFORMED_EVENTS_CACHE_TTL = 2 * 60 * 1000;
const transformedEventsCache = new Map();

const EXCLUDED_EVENT_PATTERNS = [
  /\bgroup tickets?\b/i,
  /\bparking\b/i,
  /\bvip(?:\s+experience|\s+package)?\b/i,
  /\bfast lane\b/i,
  /\bupgrade\b/i,
];

const createSearchCacheKey = (keyword, options = {}) => {
  const normalizedOptions = {};

  Object.keys(options || {})
    .sort()
    .forEach(key => {
      const value = options[key];
      if (value !== undefined && value !== null && value !== '') {
        normalizedOptions[key] = value;
      }
    });

  return JSON.stringify({
    keyword: String(keyword || '')
      .trim()
      .toLowerCase(),
    options: normalizedOptions,
  });
};

export const searchEvents = async (keyword, options = {}) => {
  try {
    // Skip API call if keyword is empty and no specific IDs are provided
    const trimmedKeyword = (keyword || '').trim();
    if (!trimmedKeyword && !options.attractionId && !options.ticketmasterVenueId) {
      return [];
    }

    const searchParams = {
      keyword: trimmedKeyword,
      size: options.size || 20,
      page: options.page || 0,
      countryCode: options.countryCode || 'US',
      stateCode: options.stateCode || '',
      city: options.city || '',
      classificationName: options.classificationName || '',
      attractionId: options.attractionId || '',
      ticketmasterVenueId: options.ticketmasterVenueId || '',
      sort: options.sort || 'date,asc',
      source: options.source || 'ticketmaster',
      includeTBA: options.includeTBA || 'no',
      includeTBD: options.includeTBD || 'no',
      startDateTime: options.startDateTime || new Date().toISOString(),
      liveListingsOnly: options.liveListingsOnly ?? true,
    };

    return await getTicketEvents(searchParams);
  } catch (error) {
    // Ticketmaster API error
    throw error;
  }
};

/**
 * Get event details by ID
 * @param {string} eventId - Ticketmaster event ID
 * @returns {Promise<Object>} Event details from Ticketmaster
 */
export const getEventDetails = async eventId => {
  try {
    return await getTicketEventDetails({ eventId });
  } catch (error) {
    console.error('Error getting event details:', error);
    throw error;
  }
};

/**
 * Transform Ticketmaster event data to match the existing listing format
 * This ensures compatibility with existing components
 * @param {Object} ticketmasterEvent - Raw event data from Ticketmaster
 * @returns {Object} Transformed event data compatible with existing components
 */
/**
 * Format date for display (e.g., "FEB 15")
 */
const formatTicketmasterEventDate = dateString => {
  const parts = getLocalDateParts(dateString);
  if (!parts) return null;

  return {
    month: parts.monthShort.toUpperCase(),
    day: parts.day,
    fullDate: parts.dateOnly,
    localDate: parts.dateOnly,
    weekday: parts.weekday,
    year: parts.year,
  };
};

const shouldIncludeTicketmasterEvent = event => {
  const title = event?.name || event?.title || event?.attributes?.title || '';
  if (EXCLUDED_EVENT_PATTERNS.some(pattern => pattern.test(title))) {
    return false;
  }

  const { localDate } = getTicketmasterLocalParts(event);
  const dateValue = localDate || event?.dates || event?.eventDate?.fullDate || null;
  if (!dateValue) {
    return false;
  }

  const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(String(dateValue))
    ? new Date(`${dateValue}T00:00:00Z`)
    : new Date(dateValue);

  return !Number.isNaN(parsedDate.getTime());
};

export const transformTicketmasterEvent = ticketmasterEvent => {
  const event = ticketmasterEvent || {};

  // Backend returns processed format: venues array instead of _embedded.venues
  const venue = event.venues?.[0] || {};

  // Get coordinates from venue data (uses city mapping if direct coords not available)
  const venueCoords = getVenueCoordinates(venue);
  const images = event.images || [];
  const priceRanges = event.priceRanges || [];

  // Use the same image selection logic as the 'sell tickets' flow
  // Filter for 16:9 images and find the largest one
  const images_16_9 = images.filter(image => image.ratio === '16_9');
  const largestImage = images_16_9.reduce(
    (largest, current) => (!largest || current.width > largest.width ? current : largest),
    null
  );

  // Get the best event image URL (same logic as sell tickets flow)
  const eventImageUrl = largestImage ? largestImage.url : images[0]?.url || null;

  // Format the event date for display (backend processed format)
  const {
    localDate: ticketmasterLocalDate,
    localTime: ticketmasterLocalTime,
    timezone,
  } = getTicketmasterLocalParts(event);
  const formattedDate =
    event.eventDate?.month && event.eventDate?.day
      ? {
          ...event.eventDate,
          fullDate: ticketmasterLocalDate || event.eventDate.fullDate,
          localDate: ticketmasterLocalDate || event.eventDate.localDate || null,
          localTime: ticketmasterLocalTime || event.eventDate.localTime || null,
        }
      : formatTicketmasterEventDate(
          ticketmasterLocalDate || event.eventDate?.fullDate || event.dates
        );
  const eventTime =
    formatLocalTimeForDisplay(ticketmasterLocalTime || event.eventDate?.localTime) ||
    event.time ||
    null;

  // Format venue information (backend processed format)
  const venueName = venue.name || 'Venue TBD';
  const venueLocation =
    venue.city && venue.state ? `${venue.city}, ${venue.state}` : venue.city || venue.state || '';

  // Get price information
  const minPrice = priceRanges.length > 0 ? Math.min(...priceRanges.map(p => p.min)) : null;
  const maxPrice = priceRanges.length > 0 ? Math.max(...priceRanges.map(p => p.max)) : null;

  // Process images from Ticketmaster data for use as listing backgrounds
  const processedImages =
    images && images.length > 0
      ? images.map(img => ({
          id: new UUID(`${event.id}-img-${img.width}x${img.height}`),
          type: 'image',
          attributes: {
            variants: {
              scaled: {
                name: `ticketmaster-${event.id}-${img.width}x${img.height}`,
                width: img.width,
                height: img.height,
                url: img.url,
              },
            },
          },
        }))
      : [];

  return {
    id: new UUID(event.id), // Convert string ID to UUID object to match local listing format
    type: 'listing',
    attributes: {
      title: event.name || 'Event',
      description: event.info || event.pleaseNote || `${event.name} at ${venue.name}`,
      price: minPrice ? { amount: Math.round(minPrice * 100), currency: 'USD' } : null,
      publicData: {
        // Dynamic event data (as requested)
        eventDate: formattedDate,
        source: 'ticketmaster',
        imageUrl: eventImageUrl,
        venueName: venueName,
        venueLocation: venueLocation,
        ticketUrl: event.url || 'https://www.ticketmaster.com', // Specific Ticketmaster event URL when available
        externalUrl: event.url || null,

        // Additional event details
        category: 'events',
        eventType: event.classifications?.[0]?.segment?.name || 'Event',
        genre: event.classifications?.[0]?.genre?.name,
        subGenre: event.classifications?.[0]?.subGenre?.name,
        city: venue.city,
        state: venue.stateCode,
        country: venue.countryCode || 'US',
        address: venue.address,
        postalCode: venue.postalCode,
        // Use coordinates from venue or city mapping (calculated above)
        location: venueCoords,
        // Also add flat lat/lng for easier access
        lat: venueCoords?.lat,
        lng: venueCoords?.lng,
        date: ticketmasterLocalDate || event.dates, // Backend processed format
        event_date: ticketmasterLocalDate || null,
        event_time: ticketmasterLocalTime || null,
        localDate: ticketmasterLocalDate || null,
        localTime: ticketmasterLocalTime || null,
        eventTime,
        time: eventTime, // Backend may include time separately
        timezone: timezone || event.timezone || null,
        status: event.status?.code || null,
        sales: event.sales || null,
        ticketmasterId: event.id,
        ticketmasterUrl: event.url,
        priceRange:
          priceRanges.length > 0
            ? {
                min: minPrice,
                max: maxPrice,
                currency: priceRanges[0].currency,
              }
            : null,
        seatmap: event.seatmap?.staticUrl,
        accessibility: venue.accessibleSeatingDetail,
        parkingDetail: venue.parkingDetail,
        generalInfo: venue.generalInfo,
      },
    },
    images: processedImages,
    relationships: {},
    // Add simple date attributes like other attributes
    date: (() => {
      if (formattedDate?.weekday) {
        return formattedDate.weekday;
      }
      return 'Date TBD';
    })(),
    dateNum: (() => {
      if (formattedDate?.month && formattedDate?.day) {
        return `${formattedDate.month} ${formattedDate.day}`;
      }
      return 'TBD';
    })(),
    // Legacy format for backward compatibility
    title: event.name || 'Event',
    venue: venue.name || 'Venue TBD',
    location: venue.city
      ? `${venue.city}, ${venue.stateCode || venue.countryCode}`
      : 'Location TBD',
    time: eventTime || 'Time TBD',
    price: minPrice || 0,
    image: eventImageUrl,
    ticketmasterId: event.id,
    ticketmasterUrl: event.url,
  };
};

/**
 * Search events and transform them to the expected format
 * @param {string} keyword - Search keyword
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Array of transformed events
 */
export const searchAndTransformEvents = async (keyword, options = {}) => {
  try {
    const cacheKey = createSearchCacheKey(keyword, options);
    const cachedEntry = transformedEventsCache.get(cacheKey);

    if (
      cachedEntry &&
      cachedEntry.events &&
      Date.now() - cachedEntry.timestamp < TRANSFORMED_EVENTS_CACHE_TTL
    ) {
      return cachedEntry.events;
    }

    const response = await searchEvents(keyword, options);

    // Backend already returns processed events array, not the raw Ticketmaster format
    const events = Array.isArray(response) ? response : [];

    const transformedEvents = events
      .filter(shouldIncludeTicketmasterEvent)
      .map(event => transformTicketmasterEvent(event));
    transformedEventsCache.set(cacheKey, {
      events: transformedEvents,
      timestamp: Date.now(),
    });

    return transformedEvents;
  } catch (error) {
    console.error('🔄 TRANSFORM - Error searching and transforming events:', error);
    throw error;
  }
};
